import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { createForm, formShape } from 'rc-form';
import styled from 'styled-components';
import UserProfileSelector from '../../../../UserProfileSelector';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Button from '../../../../SolvPatternLibrary/Button';
import { fontFamily, fontSize } from '../../../../../constants/text';
import { colors } from '../../../../../constants/colors';
import RecentlyBooked from '../../components/RecentlyBooked';
import { CDP_TOUCH_PRIMARY_ACTION } from '../../../../../core/analytics/events';
import { analyticsTrackEvent } from '../../../../../core/analytics';
import { isEmptyObject, safeGet } from '../../../../../core/util/object';
import BookingTimeSelector from '../../components/BookingTimeSelector';
import PeakTimeAlert from '../../components/PeakTimeAlert';
import moment from 'moment-timezone';
import AutosuggestTextField from '../../../../SolvPatternLibrary/AutosuggestTextField';
import { isEmpty } from '../../../../../core/util/empty';
import {
  conditionallyToggleEmergencyMessage,
  getDefaultReasonForVisitForLocation,
} from '../../../../../core/util/symptoms';
import { shouldShowBirthSexInput } from '../../../../../core/util/location';
import SelectTextField from '../../../../SolvPatternLibrary/SelectTextField';
import { BIRTH_SEX_FEMALE, BIRTH_SEX_MALE, ORIGIN_BOOKING_WIDGET } from '../../../../../constants';
import EmergencyMessage from '../../../../EmergencyModal/EmergencyMessage';
import { EMPTY_ARRAY } from '../../../../../core/util/array';
import DateTime from '../../../../DateTime';
import {
  BOOK_MODULE_TITLE,
  getCustomization,
  shouldShowFeature,
  SHOW_DATES_ON_BOOK_BUTTON,
} from '../../../util/customization';
import withAuthentication from '../../../../../core/login/withAuthentication';
import { EMPTY_STRING } from '../../../../../core/util/string';

const BOOK_ONLINE = 'Book Online';
const StyledUserProfileSelector = styled(UserProfileSelector)`
  width: 90%;
  margin: 10px 15px 0px;
`;

const BirthSexSelector = styled(SelectTextField)`
  width: 90%;
  margin: 10px 15px 0px;
`;

const Root = styled.form`
  width: 100%;
  box-sizing: border-box;
  border: ${colors.greyHounds} 1px solid;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 0 0 auto;
`;

const BookInstantly = styled.div`
  font-family: ${fontFamily.medium};
  font-size: ${fontSize.medium};
  padding: 20px 10px;
  background-color: ${colors.whiteGrey};
  color: ${colors.squid};
  text-align: center;
  border-bottom: 1px solid ${colors.greyHounds};
  letter-spacing: -0.23px;
`;

const BookCTAContainer = styled.div`
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-content: center;
`;

const BookButtonContainer = styled.div`
  padding: 20px 5% 16px;
  border-top: 1px solid #eaeae8;
`;

const StyledRecentlyBooked = styled(RecentlyBooked)`
  padding-top: 20px;
`;

const BookButton = styled(Button)`
  width: 100%;
  height: 44px;
  box-shadow: none;
  padding: 0 18px;
  font-size: ${fontSize.default};
  font-weight: ${fontFamily.bold};
  line-height: 18px;
`;

const BookButtonContent = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const BookButtonAppointment = styled.span`
  font-family: ${fontFamily.medium};
  font-size: ${fontSize.mediumSmall};
`;

const NextContainer = styled.span`
  display: flex;
  align-items: center;
`;

const StyledReasonForVisit = styled(AutosuggestTextField)`
  width: 90%;
  margin: 0 15px;
`;

const suggestionsTheme = {
  container: {
    flexGrow: 1,
    position: 'relative',
  },
  suggestionsContainerOpen: {
    position: 'absolute',
    zIndex: 10,
    marginTop: 0,
    left: '15px',
    right: '15px',
    width: '90%',
  },
  suggestion: {
    display: 'block',
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
};

class BookModule extends PureComponent {
  static propTypes = {
    location: PropTypes.object.isRequired,
    newBooking: PropTypes.object,
    form: formShape.isRequired,
    handleRequestAppointmentClick: PropTypes.func.isRequired,
    fetchLocationSlotsData: PropTypes.func,
    setBookingProfile: PropTypes.func,
    autoFocus: PropTypes.bool,
    initialValues: PropTypes.object,
    accountSummary: PropTypes.object,
    isLoggedIn: PropTypes.bool.isRequired,
    login: PropTypes.object,
    className: PropTypes.string,
    fetchSuggestions: PropTypes.func,
    suggestions: PropTypes.array,
    reasonForVisit: PropTypes.string,
  };

  constructor(props) {
    super(props);

    const appointmentDate = safeGet(props.newBooking)('booking.appointmentTime');

    this.moduleTitle = getCustomization(BOOK_MODULE_TITLE, { location: props.location }).text;
    this.state = {
      submitting: false,
      errors: {},
      buttonStatus: 'initial',
      submitButtonDisabled: !appointmentDate,
      errorDialogOpen: false,
      selectedSlot: appointmentDate ? { appointmentDate } : null,
      selectedDate: null,
      trackingSelectionProperties: null,
      shouldRenderSuggestions: true,
      showEmergencyCopy: false,
    };
  }

  onSubmit = e => {
    e && e.preventDefault();
    const { id, uclId } = this.props.location;
    const trackingProperties = {
      ...this.state.trackingSelectionProperties,
      action: BOOK_ONLINE,
      locationId: id,
      uclId,
    };
    analyticsTrackEvent(CDP_TOUCH_PRIMARY_ACTION, trackingProperties);
    this.handleRequestAppointmentClick();
  };

  onSuggestionSelected = () => {
    this.setState({ shouldRenderSuggestions: false });
  };

  onFieldChange = (value, fieldName) => {
    if (!this.state.shouldRenderSuggestions) {
      this.setState({ shouldRenderSuggestions: true });
    }

    const { form } = this.props;
    const { errors } = this.state;
    this.setState({ errors: { ...errors, [fieldName]: false } });
    form.setFieldsValue({ [fieldName]: value });
    form.validateFields(this.enableFormSubmissionIfNoErrors);
  };

  onBlur = fieldName => {
    const { form } = this.props;
    if (fieldName === 'reasonForVisit') {
      conditionallyToggleEmergencyMessage(
        form.getFieldValue('reasonForVisit'),
        this.toggleEmergencyCopy
      );
    }

    form.validateFields([fieldName], {}, errors => {
      this.enableFormSubmissionIfNoErrors(errors);
      if (isEmpty(errors)) {
        this.setState({ errors: { [fieldName]: false } });
      } else {
        this.setState({ errors: { [fieldName]: errors[fieldName] } });
      }
    });
  };

  onReasonForVisitTextInputChange = (e, suggestionSelected) => {
    this.props.fetchSuggestions(suggestionSelected.newValue);
    this.onFieldChange(suggestionSelected.newValue, 'reasonForVisit');
  };

  onShouldRenderSuggestionsCallback = () => this.state.shouldRenderSuggestions;

  getSuggestions = () => {
    const { suggestions } = this.props;
    return suggestions
      ? suggestions.map(suggestion => ({
          label: suggestion.label,
          value: suggestion.value,
        }))
      : [];
  };

  getAppointmentTimeFormat = () => {
    const {
      location: { timeZone },
    } = this.props;
    const { selectedSlot } = this.state;
    if (!selectedSlot) return null;

    return DateTime.format(selectedSlot.appointmentDate, timeZone)('h:mm a, ddd, MMMM D');
  };

  getInitialValueForReasonForVisit = () => {
    const { isLoggedIn, location, reasonForVisit } = this.props;

    if (reasonForVisit) return reasonForVisit;

    if (isLoggedIn) return EMPTY_STRING;

    return getDefaultReasonForVisitForLocation(location);
  };

  toggleEmergencyCopy = value => {
    this.setState({ showEmergencyCopy: value });
  };

  handleRequestAppointmentClick = () => {
    const { handleRequestAppointmentClick, login, form, location } = this.props;
    const {
      selectedSlot: { appointmentDate },
    } = this.state;
    const reasonForVisit = form.getFieldValue('reasonForVisit');
    handleRequestAppointmentClick(appointmentDate, login, reasonForVisit, location);
  };

  birthSexOptions = () => [
    {
      key: BIRTH_SEX_MALE,
      value: BIRTH_SEX_MALE,
      label: BIRTH_SEX_MALE,
    },
    {
      key: BIRTH_SEX_FEMALE,
      value: BIRTH_SEX_FEMALE,
      label: BIRTH_SEX_FEMALE,
    },
  ];

  canShowPeakAlert = () => {
    const { selectedDate, selectedSlot } = this.state;
    const {
      location: { timeZone },
    } = this.props;
    const selectedDateEqualToSelectedSlotAppointmentDate =
      selectedDate && selectedSlot
        ? selectedDate.tz(timeZone).date() ===
          moment.tz(selectedSlot.appointmentDate, timeZone).date()
        : true;
    return selectedSlot && selectedDateEqualToSelectedSlotAppointmentDate;
  };

  handleSelectSlot = (selected, selectionProperties) => {
    const slot = Array.isArray(selected) ? selected[0] : selected;
    const { form } = this.props;
    this.setState({
      selectedSlot: slot,
      trackingSelectionProperties: selectionProperties,
    });
    form.setFieldsValue({ appointmentDate: slot });
    form.validateFields(this.enableFormSubmissionIfNoErrors);
  };

  handleSelectedDate = selectedDate => this.setState({ selectedDate });

  handleChangeUserProfile = selectedUserProfile => {
    const clickedUserProfileId = selectedUserProfile.value;
    this.props.setBookingProfile(this.props.accountSummary, clickedUserProfileId);
    this.onFieldChange(selectedUserProfile, 'userProfile');
  };

  shouldShowAdvancedText = () => {
    const { location } = this.props;
    const { selectedSlot } = this.state;

    // feature enabled and slot selected date > 2 days out
    return (
      shouldShowFeature(SHOW_DATES_ON_BOOK_BUTTON, location) &&
      location &&
      selectedSlot &&
      selectedSlot.appointmentDate &&
      moment()
        .tz(location.timeZone)
        .add(2, 'days')
        .startOf('day')
        .valueOf() <
        moment(selectedSlot.appointmentDate)
          .tz(location.timeZone)
          .valueOf()
    );
  };

  shouldShowBirthSexSelector = () => {
    const { location, isLoggedIn } = this.props;
    return isLoggedIn && shouldShowBirthSexInput(location);
  };

  shouldShowRecentlyBooked = () => {
    const origin = safeGet(this.props.newBooking)('booking.origin');
    return origin !== ORIGIN_BOOKING_WIDGET;
  };

  enableFormSubmissionIfNoErrors = errors =>
    isEmptyObject(errors)
      ? this.state.submitButtonDisabled && this.setState({ submitButtonDisabled: false })
      : this.state.submitButtonDisabled || this.setState({ submitButtonDisabled: true });

  render() {
    const {
      location,
      form,
      fetchLocationSlotsData,
      accountSummary,
      isLoggedIn,
      className,
      autoFocus,
    } = this.props;
    const { errors, selectedSlot, buttonStatus, submitButtonDisabled } = this.state;
    return (
      <Root className={className} onSubmit={this.onSubmit}>
        <BookInstantly>{this.moduleTitle}</BookInstantly>
        <BookCTAContainer>
          <StyledReasonForVisit
            {...form.getFieldProps('reasonForVisit', {
              rules: [{ required: true }],
              initialValue: this.getInitialValueForReasonForVisit(),
            })}
            suggestions={this.getSuggestions()}
            label="Reason for Visit"
            fullWidth
            autoFocus={autoFocus}
            error={Boolean(errors.reasonForVisit)}
            onChange={this.onReasonForVisitTextInputChange}
            shouldRenderSuggestions={this.onShouldRenderSuggestionsCallback}
            onSuggestionSelected={this.onSuggestionSelected}
            onBlur={() => this.onBlur('reasonForVisit')}
            styleTheme={suggestionsTheme}
          />
          {this.state.showEmergencyCopy && <EmergencyMessage />}
          {isLoggedIn && (
            <StyledUserProfileSelector
              accountSummary={accountSummary}
              handleChangeUserProfile={this.handleChangeUserProfile}
            />
          )}
          {this.shouldShowBirthSexSelector() && (
            <BirthSexSelector
              {...form.getFieldProps('birthSex', {
                rules: [{ required: true }],
                initialValue: BIRTH_SEX_MALE,
              })}
              label="Birth Sex"
              fullWidth
              error={Boolean(errors.birthSex)}
              onChange={({ target: { value } }) => this.onFieldChange(value, 'birthSex')}
              selectOptions={this.birthSexOptions()}
              data-testid="birthSexInput"
            />
          )}
          <BookingTimeSelector
            {...form.getFieldProps('appointmentDate', {
              rules: [{ required: true }],
            })}
            fetchLocationSlotsData={fetchLocationSlotsData}
            selectedSlots={selectedSlot ? [selectedSlot] : EMPTY_ARRAY}
            location={location}
            handleSelectSlot={this.handleSelectSlot}
            handleSelectedDate={this.handleSelectedDate}
          />
          <BookButtonContainer>
            {this.canShowPeakAlert() && <PeakTimeAlert slot={selectedSlot} />}
            <BookButton
              data-testid="cdpBookingFormSubmitBtn"
              variation={'pinkPrimary'}
              animationEnabled
              status={buttonStatus}
              type="submit"
              disabled={submitButtonDisabled}
            >
              {this.shouldShowAdvancedText() ? (
                <BookButtonContent>
                  <BookButtonAppointment>{this.getAppointmentTimeFormat()}</BookButtonAppointment>
                  <NextContainer>
                    Next <ChevronRight />
                  </NextContainer>
                </BookButtonContent>
              ) : (
                'Next'
              )}
            </BookButton>
          </BookButtonContainer>
        </BookCTAContainer>
        {this.shouldShowRecentlyBooked() && <StyledRecentlyBooked location={location} />}
      </Root>
    );
  }
}

export default withAuthentication(createForm()(BookModule));
